import { hasOrganizationId } from "./organization-id"

export interface ClassicPathArgs {
  organizationId: string
  workspaceId: string
  /** Path below the workspace root, with or without a leading slash. */
  subpath?: string
  /** Query string, with or without a leading "?". */
  search?: string
  /** URL fragment, with or without a leading "#". */
  hash?: string
}

export type LegacyClassicPathArgs = Omit<ClassicPathArgs, "organizationId">

const normalizeSubpath = (subpath: string): string =>
  subpath === "" || subpath === "/"
    ? ""
    : subpath.startsWith("/")
    ? subpath
    : `/${subpath}`

const normalizeSearch = (search: string): string =>
  search === "" || search === "?"
    ? ""
    : search.startsWith("?")
    ? search
    : `?${search}`

const normalizeHash = (hash: string): string =>
  hash === "" || hash === "#" ? "" : hash.startsWith("#") ? hash : `#${hash}`

// The ids are single path segments, so they're encoded. `subpath`, `search`
// and `hash` are already-formed URL parts and are passed through untouched.
const encodeSegment = (value: string): string => encodeURIComponent(value)

/**
 * The org-scoped classic workspace URL, which the app redirects to the org-less
 * one. Prefer {@link buildClassicWorkspaceEntryPath} for new links.
 *
 * Pure and framework-free (no React, no Next) so redirects, tests and
 * server-side code can build the same URLs the components render.
 */
export function buildClassicPath({
  organizationId,
  workspaceId,
  subpath = "",
  search = "",
  hash = "",
}: ClassicPathArgs): string {
  const org = encodeSegment(organizationId)
  const workspace = encodeSegment(workspaceId)

  return `/organizations/${org}/workspaces/${workspace}${normalizeSubpath(
    subpath
  )}${normalizeSearch(search)}${normalizeHash(hash)}`
}

/** The classic console URL shape, with no organization segment. */
export function buildLegacyClassicPath({
  workspaceId,
  subpath = "",
  search = "",
  hash = "",
}: LegacyClassicPathArgs): string {
  const workspace = encodeSegment(workspaceId)

  return `/workspaces/${workspace}${normalizeSubpath(subpath)}${normalizeSearch(
    search
  )}${normalizeHash(hash)}`
}

/**
 * Where to land in the classic console for a workspace: always the org-less
 * shape. `organizationId` is accepted so callers that hold one still typecheck,
 * but it no longer appears in the URL.
 */
export function buildClassicWorkspaceEntryPath({
  workspaceId,
  subpath = "",
  search = "",
}: {
  organizationId?: string | null
  workspaceId: string
  subpath?: string
  /** Query string, with or without a leading "?". */
  search?: string
}): string {
  return buildLegacyClassicPath({ workspaceId, subpath, search })
}

/** The organization's own overview page. */
export function buildOrganizationPath(organizationId: string): string {
  return `/organizations/${encodeSegment(organizationId)}`
}

/** The org-scoped "create a workspace" route. */
export function buildClassicWorkspacesNewPath(organizationId: string): string {
  return `/organizations/${encodeSegment(organizationId)}/workspaces/new`
}

/** The org-less "create a workspace" URL, which `pages/workspaces/new.tsx` resolves. */
export const LEGACY_CLASSIC_WORKSPACES_NEW_PATH = "/workspaces/new"

/**
 * Where "New workspace" goes, org-scoped when the caller knows which
 * organization it is creating in and legacy-shaped when it doesn't.
 *
 * The legacy URL is not a broken link: `pages/workspaces/new.tsx` resolves it
 * against the user's organizations. This is the fallback for the handful of
 * call sites that sit outside any organization context.
 */
export function buildClassicWorkspacesNewEntryPath(
  organizationId: string | null | undefined
): string {
  if (!hasOrganizationId(organizationId)) {
    return LEGACY_CLASSIC_WORKSPACES_NEW_PATH
  }
  return buildClassicWorkspacesNewPath(organizationId)
}

/**
 * Where the "pick an organization to continue" fallback lives as a route.
 *
 * Defined here rather than in `dashboard/lib/dashboard-path` (which re-exports
 * it) because the classic console needs it too and `core` must never import
 * from `dashboard`. The route itself is flag-independent.
 */
export const CHOOSE_ORGANIZATION_PATH = "/dashboard/choose-organization"

/**
 * The part of `pathname` below the org-scoped workspace root, or "" when
 * `pathname` isn't inside that workspace. Used to rewrite a URL in place while
 * keeping whatever page the user is on.
 */
export function getClassicSubpath(
  pathname: string,
  organizationId: string,
  workspaceId: string
): string {
  const prefix = buildClassicPath({ organizationId, workspaceId })
  if (!pathname.startsWith(prefix)) return ""

  const subpath = pathname.slice(prefix.length)
  // Guard against a workspace id that is merely a prefix of the real segment:
  // "/…/workspaces/ws_10" must not read as "/…/workspaces/ws_1" plus "0".
  if (subpath !== "" && !subpath.startsWith("/")) return ""

  return subpath
}

/**
 * The part of `pathname` below the workspace root in *either* URL shape: the
 * org-scoped one when the organization is known, and the org-less one
 * otherwise.
 *
 * Callers that only know the workspace (breadcrumbs, active-nav checks) can't
 * assume which shape the current URL has — an org-scoped URL is still reachable
 * until its redirect lands — so they ask this instead of slicing prefixes by
 * hand or counting path segments.
 */
export function getClassicWorkspaceSubpath(
  pathname: string,
  {
    organizationId,
    workspaceId,
  }: { organizationId?: string | null; workspaceId: string }
): string {
  if (workspaceId === "") return ""

  if (hasOrganizationId(organizationId)) {
    const orgScoped = getClassicSubpath(pathname, organizationId, workspaceId)
    if (orgScoped !== "") return orgScoped
  }

  const legacyPrefix = buildLegacyClassicPath({ workspaceId })
  if (!pathname.startsWith(legacyPrefix)) return ""

  const subpath = pathname.slice(legacyPrefix.length)
  if (subpath !== "" && !subpath.startsWith("/")) return ""

  return subpath
}

/**
 * The pathname part of a URL-ish string: query string and fragment removed.
 *
 * Callers hand us `router.asPath` or `window.location.pathname + search`
 * interchangeably, so every matcher here normalizes first.
 */
export function stripQueryAndHash(pathname: string): string {
  const withoutHash = pathname.split("#")[0] ?? ""
  return withoutHash.split("?")[0] ?? ""
}

export interface ParsedClassicPath {
  /** The organization segment, or `null` for the legacy (org-less) shape. */
  organizationId: string | null
  workspaceId: string
  /** Path below the workspace root: "" at the root, "/devices/foo" otherwise. */
  subpath: string
}

// Both classic workspace URL shapes in one matcher: the org-less one, and the
// org-scoped one that old links still carry.
const CLASSIC_WORKSPACE_PATTERN =
  /^\/(?:organizations\/([^/]+)\/)?workspaces\/([^/]+)(\/.*)?$/

const decodeSegment = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    // A malformed escape (e.g. "%zz") is not worth throwing over: the raw
    // segment is still a usable identity for analytics and matching.
    return value
  }
}

/**
 * Split a classic console URL into its organization, workspace and subpath,
 * or `null` when `pathname` isn't a classic workspace URL at all.
 *
 * The inverse of `buildClassicPath` / `buildLegacyClassicPath`, for callers
 * that have a URL but not the ids — analytics classification, in particular.
 */
export function parseClassicPath(pathname: string): ParsedClassicPath | null {
  const match = CLASSIC_WORKSPACE_PATTERN.exec(stripQueryAndHash(pathname))
  if (match == null) return null

  const [, organizationId, workspaceId, subpath] = match
  if (workspaceId == null || workspaceId === "") return null

  return {
    organizationId:
      organizationId == null ? null : decodeSegment(organizationId),
    workspaceId: decodeSegment(workspaceId),
    subpath: subpath ?? "",
  }
}
